/**
 * Fixed header: mobile overlay menu, colour inversion over dark sections and
 * the active navigation item (scroll spy).
 *
 * <header data-header>
 *   <button data-nav-toggle aria-expanded="false">…</button>
 *   <nav data-nav><a data-nav-link href="#slug">…</a></nav>
 * </header>
 *
 * Navigation points are `.nav-anchor` elements with an id; the section a
 * point refers to is the next `[data-section]` sibling after it. Because the
 * sections are stacked (sticky), "the section under the header" is found by
 * hit-testing the page at the header's bottom edge rather than by scroll
 * offsets.
 */
import { claimed, scrollToTarget } from './scroll'

const header = document.querySelector('[data-header]')

if (header) {
  const body = document.body
  const toggles = header.querySelectorAll('[data-nav-toggle]')
  const links = [...document.querySelectorAll('[data-nav-link]')]

  // Mobile overlay ---------------------------------------------------------
  const setMenu = (open) => {
    body.classList.toggle('mobile-menu-open', open)
    toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)))
  }

  toggles.forEach((toggle) =>
    toggle.addEventListener('click', () => setMenu(!body.classList.contains('mobile-menu-open')))
  )
  links.forEach((link) => link.addEventListener('click', () => setMenu(false)))
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setMenu(false)
  })
  window.matchMedia('(min-width: 1024px)').addEventListener('change', (query) => {
    if (query.matches) setMenu(false)
  })

  // Easter egg: three quick clicks on the logo drop the plumb bob -----------
  const logo = header.querySelector('[data-logo]')
  const plumb = logo?.querySelector('[data-logo-plumb]')
  if (logo && plumb) {
    const svg = plumb.closest('svg')
    const VIEWBOX_HEIGHT = 163.571
    const BOB_TIP = 160.436 // user units, tip of the bob at rest
    const STRING_REST = 2.1 // user units between the stem and the knot
    let clicks = 0
    let timer = 0

    const drop = () => {
      if (logo.classList.contains('is-dropping')) return
      const rect = svg.getBoundingClientRect()
      const scale = rect.height / VIEWBOX_HEIGHT
      const floor = window.innerHeight - 24
      const units = Math.max(0, (floor - rect.top) / scale - BOB_TIP)
      svg.style.setProperty('--plumb-drop', `${units.toFixed(2)}px`)
      svg.style.setProperty('--plumb-string', `${(STRING_REST + units).toFixed(2)}`)
      logo.classList.add('is-dropping')
    }
    plumb.addEventListener('animationend', (event) => {
      if (event.target === plumb) logo.classList.remove('is-dropping')
    })

    logo.addEventListener('click', (event) => {
      // Only on the front page: elsewhere the logo simply leads home.
      if (!document.body.classList.contains('home')) return
      event.preventDefault()
      clicks += 1
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        clicks = 0
      }, 900)
      if (clicks >= 3) {
        clicks = 0
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) drop()
        return
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }

  // In-page anchors scroll animated (src/js/scroll.js). A module that scrolls
  // itself — the jump navigation of the text sections — has already prevented
  // the default; modifier clicks keep opening the link in a new tab.
  document.addEventListener('click', (event) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null
    if (!link) return
    const url = new URL(link.href, window.location.href)
    if (url.hash.length < 2 || url.origin !== window.location.origin) return
    if (url.pathname !== window.location.pathname) return
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)))
    if (!target) return
    event.preventDefault()
    setMenu(false)
    window.history.pushState(null, '', url.hash)
    scrollToTarget(target)
  })

  // A #hash the page is opened with, or typed into the address bar: the
  // browser's own jump knows nothing about the freeze point, so redo it —
  // instantly on load (not when history brings the visitor back to where
  // they were), animated on a hash change, taking over from the native
  // smooth scroll. Targets of the text sections scroll themselves.
  const jumpToHash = (instant) => {
    const id = decodeURIComponent(window.location.hash.slice(1))
    const target = id ? document.getElementById(id) : null
    if (target && !claimed.has(target)) scrollToTarget(target, { instant })
  }
  window.addEventListener('load', () => {
    if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') jumpToHash(true)
  })
  window.addEventListener('hashchange', () => jumpToHash(false))

  // Section under the header --------------------------------------------
  const anchorForSection = new Map()
  document.querySelectorAll('.nav-anchor[id]').forEach((anchor) => {
    let section = anchor.nextElementSibling
    while (section && !section.matches('[data-section]')) {
      section = section.nextElementSibling
    }
    if (section) anchorForSection.set(section, anchor.id)
  })

  const sectionAt = (y) => {
    const x = Math.max(1, Math.round(window.innerWidth / 2))
    for (const element of document.elementsFromPoint(x, y)) {
      if (element.closest('[data-header]')) continue
      const section = element.closest('[data-section]')
      if (section) return section
    }
    return null
  }

  let ticking = false
  const spy = () => {
    ticking = false
    const probeY = Math.min(header.offsetHeight, window.innerHeight - 1)
    const topSection = sectionAt(Math.max(1, probeY - 1))
    header.classList.toggle('is-inverted', topSection?.dataset.theme === 'dark')
    header.classList.toggle('is-scrolled', window.scrollY > 40)

    // The active item is the last navigation point whose section reached the
    // middle of the viewport.
    const midSection = sectionAt(Math.round(window.innerHeight / 2))
    let active = midSection ? anchorForSection.get(midSection) : null
    if (midSection && !active) {
      // A section without its own point belongs to the point before it.
      let previous = midSection.previousElementSibling
      while (previous && !active) {
        if (previous.matches('.nav-anchor[id]')) active = previous.id
        previous = previous.previousElementSibling
      }
    }
    links.forEach((link) => {
      const isActive = active !== null && link.getAttribute('href')?.endsWith(`#${active}`)
      link.classList.toggle('is-active', Boolean(isActive))
      if (isActive) link.setAttribute('aria-current', 'location')
      else link.removeAttribute('aria-current')
    })
  }

  const requestSpy = () => {
    if (!ticking) {
      ticking = true
      window.requestAnimationFrame(spy)
    }
  }

  window.addEventListener('scroll', requestSpy, { passive: true })
  window.addEventListener('resize', requestSpy)
  window.addEventListener('load', requestSpy)
  requestSpy()
}
